import { useTranscripts } from '@/api/customers/transcripts/getTranscripts';
import { Spinner, Table } from '@/components/Elements';
import { Button } from '@/components/ui/elements/button';
import { PolicyGate } from '@/features/auth/authorization';
import { useFileDownload } from '@/hooks/useFileDownload';
import { CallTranscript, Customer, ModelID } from '@/types';
import { getRaw } from '@/utils/api';
import { dateFromApiDateTime } from '@/utils/format';
import { CloudArrowDownIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';

interface CallLogsProps {
  customer: Customer;
  location: ModelID;
}

export const CallLogs = ({ customer, location }: CallLogsProps) => {
  const [downloadPending, setDownloadIsPending] = useState<ModelID[]>([]);
  const { download } = useFileDownload();

  const handleDownloadInvoice = async (transcript: CallTranscript) => {
    setDownloadIsPending((prev) => [...prev, transcript.id]);
    const result = await getRaw<Blob>(
      `/v4/customers/customer/${customer.id}/transcript/${transcript.id}`,
      {},
    );
    download(
      result,
      `${transcript.call_sid}-${transcript.sequence}-${transcript.id}.wav`,
      'download',
    );
    setDownloadIsPending((prev) => prev.filter((id) => id !== transcript.id));
  };

  const { data, isSuccess, isPending, isError, error } = useTranscripts({
    customer,
    location,
  });

  return (
    <>
      {isSuccess && (
        <PolicyGate policy="customers.calls">
          <Table<CallTranscript>
            label="calls"
            isPending={isPending}
            error={isError ? error : undefined}
            data={data ?? []}
            columns={[
              {
                title: 'Call ID',
                field: 'call_sid',
                Cell({ entry }) {
                  return (
                    <div>
                      <span className="font-mono">{entry.call_sid}</span>
                      {entry.sequence > 1 && <span> (Part {entry.sequence})</span>}
                      {entry.call_log?.created_at && (
                        <div className="text-sm text-gray-500">
                          Date: {dateFromApiDateTime(entry.call_log.created_at).toLocaleString()}
                        </div>
                      )}
                      {entry.call_log?.agent && (
                        <div className="text-sm text-gray-500">
                          Agent: {entry.call_log.agent.full_name}
                        </div>
                      )}
                      {entry.call_log && (
                        <div className="text-sm text-gray-500">
                          {entry.call_log.direction === 'inbound' ? 'Inbound' : 'Outbound'} call,{' '}
                          {entry.call_log.call_duration / 60} mins
                        </div>
                      )}
                    </div>
                  );
                },
              },
              {
                title: 'Download',
                field: 'recording_filepath',
                Cell({ entry }) {
                  return (
                    <>
                      <Button
                        onClick={() => handleDownloadInvoice(entry)}
                        disabled={downloadPending.includes(entry.id)}
                      >
                        {downloadPending.includes(entry.id) ? (
                          <Spinner className="text-white" />
                        ) : (
                          <CloudArrowDownIcon className="h-5 w-5" />
                        )}
                      </Button>
                    </>
                  );
                },
              },
            ]}
          />
          <div className="mb-96"> </div>
        </PolicyGate>
      )}
    </>
  );
};
